import { Loader2, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Fragment, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Textarea } from '@apps/web/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { cn } from '@apps/web/lib/utils';

export type MessageFeedbackLabel = 'good' | 'bad';

/** Matches the comment length check on phoebe_agent_message_feedback. */
const COMMENT_MAX_LENGTH = 1000;

/**
 * Thumbs up/down on a single assistant answer, plus the note a thumbs-down
 * collects. Owns only its draft state; the saved rating arrives via `feedback`
 * and persistence is the caller's job.
 *
 * A thumbs-up saves straight away. A thumbs-down opens the note first, because
 * in a training org the note IS the rating — `requiresComment` holds Send
 * disabled until something is written so a bare downvote that teaches Phoebe
 * nothing can't be recorded.
 *
 * No path sends an empty comment: Send is disabled until something is written,
 * and thumbs-up and Skip omit the field. Re-affirming a rating therefore keeps
 * the saved note — but flipping the label retires it server-side, because the
 * note explained the verdict that no longer stands.
 */
export function MessageFeedbackControls({
    feedback,
    requiresComment,
    onSubmit,
}: {
    readonly feedback: {
        readonly label: MessageFeedbackLabel;
        readonly comment: string | null;
    } | null;
    readonly requiresComment: boolean;
    readonly onSubmit: (submission: {
        readonly label: MessageFeedbackLabel;
        readonly comment?: string;
    }) => Promise<boolean>;
}) {
    const [isNoteOpen, setIsNoteOpen] = useState(false);
    const [comment, setComment] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);

    async function save(
        label: MessageFeedbackLabel,
        submittedComment?: string,
    ) {
        setIsSaving(true);
        setHasFailed(false);
        try {
            const saved = await onSubmit({ label, comment: submittedComment });
            if (saved) {
                setIsNoteOpen(false);
                return;
            }
            setHasFailed(true);
        } catch {
            // The caller resolves false rather than throwing, but a rejection
            // must not strand the buttons disabled for the rest of the session.
            setHasFailed(true);
        } finally {
            setIsSaving(false);
        }
    }

    const trimmedComment = comment.trim();
    // Send always carries a note. With an empty box it would post `comment: ''`,
    // which the API reads as "replace" and would wipe a note already saved.
    const isSendDisabled = isSaving || trimmedComment.length === 0;

    const failureMessage = hasFailed ? (
        <p className="text-destructive text-sm">
            We couldn’t save your feedback.
        </p>
    ) : null;

    return (
        <Fragment>
            <div className="inline-flex items-center gap-0.5">
                <Tooltip>
                    <TooltipTrigger asChild={true}>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="Helpful"
                            aria-pressed={feedback?.label === 'good'}
                            disabled={isSaving}
                            className={cn(
                                'size-6 rounded-[5px] transition-colors',
                                feedback?.label === 'good'
                                    ? 'text-foreground'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                            onClick={() => {
                                void save('good');
                            }}
                        >
                            {isSaving && !isNoteOpen ? (
                                <Loader2 className="size-3 animate-spin" />
                            ) : (
                                <ThumbsUp
                                    className={cn(
                                        'size-3',
                                        // Solid once recorded, so a rating you
                                        // already gave reads at a glance.
                                        feedback?.label === 'good' &&
                                            'fill-current',
                                    )}
                                />
                            )}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Helpful</TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild={true}>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="Not helpful"
                            aria-pressed={feedback?.label === 'bad'}
                            disabled={isSaving}
                            className={cn(
                                'size-6 rounded-[5px] transition-colors',
                                feedback?.label === 'bad'
                                    ? 'text-foreground'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                            onClick={() => {
                                setComment(feedback?.comment ?? '');
                                setHasFailed(false);
                                setIsNoteOpen(true);
                            }}
                        >
                            <ThumbsDown
                                className={cn(
                                    'size-3',
                                    feedback?.label === 'bad' && 'fill-current',
                                )}
                            />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Not helpful</TooltipContent>
                </Tooltip>
            </div>
            {isNoteOpen ? null : failureMessage}
            <Dialog open={isNoteOpen} onOpenChange={setIsNoteOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogTitle>Share feedback with Phoebe</DialogTitle>
                    <DialogDescription className="sr-only">
                        Tell Phoebe how to handle this next time.
                    </DialogDescription>
                    <Textarea
                        aria-label="How should Phoebe handle this next time?"
                        className="min-h-28 resize-none text-sm"
                        maxLength={COMMENT_MAX_LENGTH}
                        placeholder="How should Phoebe handle this next time?"
                        value={comment}
                        disabled={isSaving}
                        autoFocus={true}
                        onChange={(event) => {
                            setComment(event.target.value);
                        }}
                    />
                    {failureMessage}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={isSaving}
                            onClick={() => {
                                if (requiresComment) {
                                    setIsNoteOpen(false);
                                    return;
                                }
                                void save('bad');
                            }}
                        >
                            {requiresComment ? 'Cancel' : 'Skip'}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            disabled={isSendDisabled}
                            onClick={() => {
                                void save('bad', trimmedComment);
                            }}
                        >
                            {isSaving ? (
                                <Loader2 className="size-3 animate-spin" />
                            ) : (
                                'Send'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Fragment>
    );
}
